/**
 * Create Relationship Modal
 *
 * PE-775: Modal for creating new object-level relationships.
 * Allows defining Lookup or Master-Detail relationships between objects.
 *
 * @component
 */

import React, { useState, useEffect } from 'react';
import { X, Link2, AlertCircle, ArrowRight, Info } from 'lucide-react';
import {
  useCreateRelationship,
  type RelationshipType,
  type CreateRelationshipInput
} from '../../../services/queries/objectRelationshipQueries';
import { useObjects } from '../../../services/queries/objectQueries';
import { useToast } from '../../../contexts/ToastContext';
import { getCustomSuffix } from '../../../config/gobodoConfig';

interface CreateRelationshipModalProps {
  sourceObjectId: string;
  sourceObjectName: string;
  sourceObjectLabel: string;
  isOpen: boolean;
  onClose: () => void;
}

export const CreateRelationshipModal: React.FC<CreateRelationshipModalProps> = ({
  sourceObjectId,
  sourceObjectName,
  sourceObjectLabel,
  isOpen,
  onClose
}) => {
  const { success, error: showError } = useToast();
  const createRelationship = useCreateRelationship();
  const { data: allObjects } = useObjects({ isActive: true });

  // Form state
  const [fieldLabel, setFieldLabel] = useState('');
  const [fieldName, setFieldName] = useState('');
  const [targetObjectId, setTargetObjectId] = useState('');
  const [relationshipType, setRelationshipType] = useState<RelationshipType>('lookup');
  const [childRelationshipName, setChildRelationshipName] = useState('');
  const [description, setDescription] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [cascadeDelete, setCascadeDelete] = useState(true);
  const [shareAccessLevel, setShareAccessLevel] = useState<'ReadOnly' | 'ReadWrite' | 'None'>('ReadWrite');
  const [lookupFilter, setLookupFilter] = useState('');

  // Auto-generate field name from label
  useEffect(() => {
    if (fieldLabel) {
      const suffix = getCustomSuffix();
      const generated = fieldLabel
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '_')
        .substring(0, 40) + suffix;
      setFieldName(generated);
    }
  }, [fieldLabel]);

  // Auto-generate child relationship name
  useEffect(() => {
    if (fieldLabel && sourceObjectLabel) {
      const generated = sourceObjectLabel.replace(/\s+/g, '') + 's';
      setChildRelationshipName(generated);
    }
  }, [fieldLabel, sourceObjectLabel]);

  // Master-detail defaults
  useEffect(() => {
    if (relationshipType === 'master-detail') {
      setIsRequired(true);
      setCascadeDelete(true);
    }
  }, [relationshipType]);

  // Reset form when closing
  useEffect(() => {
    if (!isOpen) {
      setFieldLabel('');
      setFieldName('');
      setTargetObjectId('');
      setRelationshipType('lookup');
      setChildRelationshipName('');
      setDescription('');
      setIsRequired(false);
      setCascadeDelete(true);
      setShareAccessLevel('ReadWrite');
      setLookupFilter('');
    }
  }, [isOpen]);

  // Available target objects (exclude self)
  const targetObjects = allObjects?.filter(obj => obj.id !== sourceObjectId) || [];

  // Get selected target object
  const selectedTargetObject = targetObjects.find(obj => obj.id === targetObjectId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!fieldLabel.trim()) {
      showError('Validation Error', 'Field Label is required');
      return;
    }
    if (!targetObjectId) {
      showError('Validation Error', 'Please select a target object');
      return;
    }
    if (!childRelationshipName.trim()) {
      showError('Validation Error', 'Child Relationship Name is required');
      return;
    }

    const input: CreateRelationshipInput = {
      sourceObjectId,
      sourceObjectName,
      sourceObjectLabel,
      fieldName,
      fieldLabel: fieldLabel.trim(),
      targetObjectId,
      targetObjectName: selectedTargetObject?.apiName || '',
      targetObjectLabel: selectedTargetObject?.label || '',
      relationshipType,
      cascadeDelete: relationshipType === 'master-detail' ? cascadeDelete : false,
      shareAccessLevel: relationshipType === 'master-detail' ? shareAccessLevel : 'None',
      childRelationshipName: childRelationshipName.trim(),
      description: description.trim() || undefined,
      isRequired: relationshipType === 'master-detail' ? true : isRequired,
      lookupFilter: lookupFilter.trim() || undefined
    };

    try {
      await createRelationship.mutateAsync(input);
      success('Relationship Created', `"${fieldLabel}" relationship has been created.`);
      onClose();
    } catch (err) {
      showError('Error', 'Failed to create relationship. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Link2 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">New Relationship</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Create a relationship from {sourceObjectLabel}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Relationship Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Relationship Type
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setRelationshipType('lookup')}
                  className={`p-4 border-2 rounded-lg text-left transition-colors ${
                    relationshipType === 'lookup'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                  }`}
                >
                  <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Lookup</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    Simple reference to another object. Parent record can exist independently.
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setRelationshipType('master-detail')}
                  className={`p-4 border-2 rounded-lg text-left transition-colors ${
                    relationshipType === 'master-detail'
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                  }`}
                >
                  <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Master-Detail</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    Tight relationship. Child inherits sharing and can cascade delete.
                  </div>
                </button>
              </div>
            </div>

            {/* Target Object */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Related To (Target Object) <span className="text-red-500">*</span>
              </label>
              <select
                value={targetObjectId}
                onChange={(e) => setTargetObjectId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              >
                <option value="">Select an object...</option>
                {targetObjects.map(obj => (
                  <option key={obj.id} value={obj.id}>
                    {obj.label} ({obj.apiName})
                  </option>
                ))}
              </select>
              {targetObjectId && (
                <div className="mt-2 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-medium">{sourceObjectLabel}</span>
                  <ArrowRight className="w-4 h-4" />
                  <span className="font-medium">{selectedTargetObject?.label}</span>
                </div>
              )}
            </div>

            {/* Field Label */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Field Label <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={fieldLabel}
                onChange={(e) => setFieldLabel(e.target.value)}
                placeholder="e.g., Primary Contact, Parent Account"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>

            {/* Field Name (API) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Field Name (API)
              </label>
              <input
                type="text"
                value={fieldName}
                onChange={(e) => setFieldName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Auto-generated from label. This is the API name used in code.
              </p>
            </div>

            {/* Child Relationship Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Child Relationship Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={childRelationshipName}
                onChange={(e) => setChildRelationshipName(e.target.value)}
                placeholder="e.g., Contacts, Opportunities"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Used when querying child records from the parent (e.g., Account.Contacts)
              </p>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the purpose of this relationship..."
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Lookup Options */}
            {relationshipType === 'lookup' && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isRequired"
                    checked={isRequired}
                    onChange={(e) => setIsRequired(e.target.checked)}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <label htmlFor="isRequired" className="text-sm text-gray-700 dark:text-gray-300">
                    Required - Always require a value for this lookup
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Lookup Filter (Optional)
                  </label>
                  <input
                    type="text"
                    value={lookupFilter}
                    onChange={(e) => setLookupFilter(e.target.value)}
                    placeholder="e.g., Status = 'Active'"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Filter which records appear in the lookup picker
                  </p>
                </div>
              </div>
            )}

            {/* Master-Detail Options */}
            {relationshipType === 'master-detail' && (
              <div className="space-y-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <div className="flex items-start gap-2 mb-4">
                  <Info className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-purple-800 dark:text-purple-200">
                    Master-Detail relationships are always required. The child record cannot exist without a parent.
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="cascadeDelete"
                    checked={cascadeDelete}
                    onChange={(e) => setCascadeDelete(e.target.checked)}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <label htmlFor="cascadeDelete" className="text-sm text-gray-700 dark:text-gray-300">
                    Cascade Delete - Delete child records when parent is deleted
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Sharing Access
                  </label>
                  <select
                    value={shareAccessLevel}
                    onChange={(e) => setShareAccessLevel(e.target.value as any)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="ReadWrite">Read/Write - Child inherits parent's full access</option>
                    <option value="ReadOnly">Read Only - Child inherits parent's read access</option>
                    <option value="None">None - No inherited access</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3 bg-gray-50 dark:bg-gray-800">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={createRelationship.isPending}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {createRelationship.isPending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Creating...
              </>
            ) : (
              'Create Relationship'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateRelationshipModal;
